import { DataTag } from '@/components/DataTag/DataTag'
import type { LicenseResponseBody } from '@/features/Licenses/api/licenseHooks'
import { useRevokeOrgLicense } from '@/features/Licenses/api/licenseHooks'
import { LicenseFormDialog } from '@/features/Licenses/components/LicenseFormDialog'
import {
  hasStripeBilling,
  isSelfServiceLicense,
} from '@/features/Licenses/lib/licensePredicates'
import {
  buildMutationOptions,
  mapMutationError,
} from '@/features/Licenses/lib/mutationHandlers'
import {
  Alert,
  Box,
  Button,
  Field,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

type Props = { license: LicenseResponseBody; onSuccess: () => void }

export function RevokeForm({ license, onSuccess }: Readonly<Props>) {
  const { t } = useTranslation('features', { useSuspense: false })
  const queryClient = useQueryClient()
  const [confirmText, setConfirmText] = useState('')

  const mutation = useRevokeOrgLicense(
    buildMutationOptions({
      queryClient,
      license,
      onSuccess,
      toast: {
        id: 'revoke-license-error',
        title: String(t('licenses.actions.revoke.error.title')),
        description: String(t('licenses.actions.revoke.error.generic')),
      },
      inlineStatuses: [400],
    }),
  )

  const canSubmit = confirmText === 'revoke'

  const isSelfService = isSelfServiceLicense(license)

  const inDialogError = mapMutationError(mutation, {
    inlineStatuses: [400],
    fallbackDetail: String(t('licenses.actions.revoke.error.generic')),
  })

  const handleSubmit = () => {
    if (canSubmit) {
      mutation.mutate({
        pathParams: { orgId: license.orgId, licenseId: license.id },
      })
    }
  }

  return (
    <LicenseFormDialog
      title={String(t('licenses.actions.revoke.title'))}
      errorTitle={String(t('licenses.actions.revoke.error.title'))}
      inDialogError={inDialogError}
      cancelLabel={String(t('licenses.actions.revoke.cancel'))}
      submitButton={
        <Button
          colorPalette="red"
          disabled={!canSubmit || mutation.isPending}
          loading={mutation.isPending}
          onClick={handleSubmit}
        >
          {String(t('licenses.actions.revoke.submitButton'))}
        </Button>
      }
    >
      <Text fontSize="sm">{String(t('licenses.actions.revoke.warning'))}</Text>
      {hasStripeBilling(license) ? (
        <Alert.Root status="warning">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Description>
              {String(t('licenses.actions.revoke.stripeWarning'))}
            </Alert.Description>
          </Alert.Content>
        </Alert.Root>
      ) : null}
      <Text fontSize="sm" color="text.text-subtle">
        {String(
          t(
            isSelfService
              ? 'licenses.actions.revoke.reversibilityNoteSelfService'
              : 'licenses.actions.revoke.reversibilityNoteSLG',
          ),
        )}
      </Text>
      <VStack align="start" gap={1}>
        <Text fontSize="xs" color="text.text-subtle">
          {String(t('licenses.actions.revoke.identityLabel'))}
        </Text>
        {/* No copy button: the dialog asks the operator to type `revoke`, not
            the license name, so offering to copy the name is misleading. */}
        <DataTag value={license.name ?? license.id} hasCopy={false}>
          {license.name ?? license.id}
        </DataTag>
      </VStack>
      <Field.Root>
        <Field.Label>
          {/* The label reads "Type <strong>revoke</strong> to confirm".
              Using <Trans> with a `word` component slot avoids splitting
              the translated string on the literal token (which is
              brittle for i18n — translators may quote / capitalise /
              reorder differently). The i18n key holds the full sentence
              with the placeholder element. */}
          <Trans
            i18nKey="licenses.actions.revoke.typeToConfirmLabel"
            t={t}
            components={{
              word: <Box as="strong" fontFamily="mono" />,
            }}
          />
        </Field.Label>
        <Input
          placeholder={String(
            t('licenses.actions.revoke.typeToConfirmPlaceholder'),
          )}
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          borderColor={canSubmit ? 'green.400' : undefined}
        />
      </Field.Root>
    </LicenseFormDialog>
  )
}
